// @flow
import type { Latitude, Longitude, Location } from 'types/entities';

export type Place = {
  latitude: Latitude,
  longitude: Longitude,
  address?: string,
  keywords?: string,
};

type Places = {
  itinerary: Array<Place>,
};

type BookingCore = {
  pickUpLatitude: Latitude,
  pickUpLongitude: Longitude,
  dropOffLatitude: Latitude,
  dropOffLongitude: Longitude,
};

export type BookingRequest = BookingCore & {
  taxiTypeID: string,
  paymentType: string,
  remarks?: string,
  extraDropOffs: Array<Location>,
};

export type BookingRequestV2 = Places & {
  taxiTypeID: string,
  paymentType: string,
  remarks?: string,
};

export type Booking = Places & {
  bookingID: string,
  state: number,
  passengerID?: string,
  passengerName?: string,
  driverID?: string,
  taxiTypeName: string,
};

export type BookingForDriver = {
  bookingID: string,
  driverID: string,
  state?: number,
};

export type BookingForPassenger = {
  bookingID: string,
  passengerID: string,
  state?: number,
};

export type ActiveBookingDto = BookingForDriver | BookingForPassenger;

// BookingStateUNKNOWN refers to an unknown booking state.
export const BOOKING_STATE_UNKNOWN: number = 0;
// BOOKING_STATE_NEW refers to a new booking.
export const BOOKING_STATE_NEW: number = 1;
// BOOKING_STATE_SURFACED refers to a booking broadcasted to all nominated drivers.
export const BOOKING_STATE_SURFACED: number = 2;
// BOOKING_STATE_BID refers to a booking which was bid by the given driver.
export const BOOKING_STATE_BID: number = 3;
// BOOKING_STATE_DECLINED refers to a booking which was declined by the given driver.
export const BOOKING_STATE_DECLINED: number = 4;
// BOOKING_STATE_UNREAD is the same as the `bookingCandidateStateUnread` state.
export const BOOKING_STATE_UNREAD: number = 5;
// BOOKING_STATE_IGNORED refers to an assignment booking which was ignored by the given driver.
export const BOOKING_STATE_IGNORED: number = 6;
// BOOKING_STATE_BID_CANCELLED refers to a booking where the bid was cancelled by the given driver.
export const BOOKING_STATE_BID_CANCELLED: number = 7;
// BOOKING_STATE_JOB_CANCELLED refers to a booking which was cancelled by the passenger.
export const BOOKING_STATE_JOB_CANCELLED: number = 8;
// BOOKING_STATE_CONFIRMED refers to a booking where the awarded
// message has been sent to the given driver.
// Booking Service uses CONFIRMED to indicate the driver is AWARDED.
export const BOOKING_STATE_CONFIRMED: number = 9;
// BOOKING_STATE_AWARDED refers to a booking which was awarded to the given driver.
export const BOOKING_STATE_AWARDED: number = 10;
// BOOKING_STATE_ADVANCE_AWARDED refers to a booking which was awarded in
// advance to the given driver.
export const BOOKING_STATE_ADVANCE_AWARDED: number = 11;
// BOOKING_STATE_REJECTED refers to a booking which was not assigned to the given driver.
export const BOOKING_STATE_REJECTED: number = 12;
// BOOKING_STATE_CLOSED is the same as the `bookingCandidateStateClosed` state.
export const BOOKING_STATE_CLOSED: number = 13;
// BOOKING_STATE_PICKING_UP refers to a booking which the driver is
// travelling to pick up the passenger.
export const BOOKING_STATE_PICKING_UP: number = 14;
// BOOKING_STATE_DROPPING_OFF refers to a booking which the driver is travelling
// to the passenger's destination.
export const BOOKING_STATE_DROPPING_OFF: number = 15;
// BOOKING_STATE_COMPLETED refers to a booking which was completed.
export const BOOKING_STATE_COMPLETED: number = 16;
// BOOKING_STATE_DRIVER_CANCELLED refers to a booking which was cancelled by the driver.
export const BOOKING_STATE_DRIVER_CANCELLED: number = 17;
// BOOKING_STATE_UNALLOCATED refers to a booking which was not assigned to any driver.
export const BOOKING_STATE_UNALLOCATED: number = 18;
// BOOKING_STATE_RECYCLE refers to a booking which was recycled (change to another strategy)
// if there is no candidate found during nomination.
export const BOOKING_STATE_RECYCLE: number = 19;

export const bookingStateMapping = {
  [BOOKING_STATE_UNKNOWN]: 'UNKNOWN',
  [BOOKING_STATE_NEW]: 'NEW',
  [BOOKING_STATE_SURFACED]: 'SURFACED',
  [BOOKING_STATE_BID]: 'BID',
  [BOOKING_STATE_DECLINED]: 'DECLINED',
  [BOOKING_STATE_UNREAD]: 'UNREAD',
  [BOOKING_STATE_IGNORED]: 'IGNORED',
  [BOOKING_STATE_BID_CANCELLED]: 'BID_CANCELLED',
  [BOOKING_STATE_JOB_CANCELLED]: 'JOB_CANCELLED',
  [BOOKING_STATE_CONFIRMED]: 'CONFIRMED',
  [BOOKING_STATE_AWARDED]: 'AWARDED',
  [BOOKING_STATE_ADVANCE_AWARDED]: 'ADVANCE_AWARDED',
  [BOOKING_STATE_REJECTED]: 'REJECTED',
  [BOOKING_STATE_CLOSED]: 'CLOSED',
  [BOOKING_STATE_PICKING_UP]: 'PICKING_UP',
  [BOOKING_STATE_DROPPING_OFF]: 'DROPPING_OFF',
  [BOOKING_STATE_COMPLETED]: 'COMPLETED',
  [BOOKING_STATE_DRIVER_CANCELLED]: 'DRIVER_CANCELLED',
  [BOOKING_STATE_UNALLOCATED]: 'UNALLOCATED',
  [BOOKING_STATE_RECYCLE]: 'RECYCLED',
};

export const BOOKING_MARKER_PICKUP: number = 1;
export const BOOKING_MARKER_DROPOFF: number = 2;



/** WEBPACK FOOTER **
 ** ./src/js/types/bookings.js
 **/